import { Fragment } from "react";

import { type Block, type Inline, Markdown } from "@/lib/markdown";

const headingStyle: Record<1 | 2 | 3, { tag: "h2" | "h3" | "h4"; className: string }> = {
	1: {
		tag: "h2",
		className:
			"mb-3 mt-7 font-display text-2xl font-semibold tracking-tight text-foreground first:mt-0",
	},
	2: {
		tag: "h3",
		className: "mb-2.5 mt-6 font-display text-xl font-semibold text-foreground first:mt-0",
	},
	3: {
		tag: "h4",
		className: "mb-2 mt-5 font-display text-base font-semibold text-foreground first:mt-0",
	},
};

function InlinePart({ part }: { part: Inline }) {
	switch (part.kind) {
		case "text":
			return <>{part.text}</>;
		case "bold":
			return <strong className="font-semibold text-foreground">{part.text}</strong>;
		case "italic":
			return <em>{part.text}</em>;
		case "link":
			return (
				<a
					href={part.href}
					target="_blank"
					rel="noopener noreferrer"
					className="text-primary underline decoration-primary/40 underline-offset-2 transition-colors hover:decoration-primary"
				>
					{part.text}
				</a>
			);
	}
}

function Inlines({ parts }: { parts: Inline[] }) {
	return (
		<>
			{parts.map((part, index) => (
				<InlinePart key={index} part={part} />
			))}
		</>
	);
}

function BlockView({ block }: { block: Block }) {
	switch (block.kind) {
		case "heading": {
			const { tag: Tag, className } = headingStyle[block.level];

			return (
				<Tag className={className}>
					<Inlines parts={block.content} />
				</Tag>
			);
		}
		case "list":
			return (
				<ul className="mb-4 list-disc space-y-1.5 pl-5 text-base leading-relaxed text-foreground last:mb-0">
					{block.items.map((item, index) => (
						<li key={index}>
							<Inlines parts={item} />
						</li>
					))}
				</ul>
			);
		case "paragraph":
			return (
				<p className="mb-4 text-base leading-relaxed text-foreground last:mb-0">
					{block.lines.map((line, index) => (
						<Fragment key={index}>
							{!!index && <br />}
							<Inlines parts={line} />
						</Fragment>
					))}
				</p>
			);
	}
}

export function MarkdownView({ md }: { md: string }) {
	return (
		<>
			{Markdown.blocks(md).map((block, index) => (
				<BlockView key={index} block={block} />
			))}
		</>
	);
}
